import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'app-approve-consultants',
  templateUrl: './approve-consultants.component.html',
  styleUrls: ['./approve-consultants.component.scss'],
})
export class ApproveConsultantsComponent implements OnInit {
  consultants: any[] = [];
  loading = false;
  errorMessage = '';

  page = 1;
  limit = 20;
  total = 0;
  totalPages = 1;

  approvedConsultants: any[] = [];
  approvedLoading = false;
  approvedErrorMessage = "";
  approvedPage = 1;
  approvedLimit = 20;
  approvedTotal = 0;
  approvedTotalPages = 1;

  rejectedConsultants: any[] = [];
  rejectedLoading = false;
  rejectedErrorMessage = "";
  rejectedPage = 1;
  rejectedLimit = 20;
  rejectedTotal = 0;
  rejectedTotalPages = 1;

  showRejectDialog = false;
  selectedConsultant: any = null;
  rejectionReason = '';
  actionLoadingId = '';

  constructor(
    private authenticationService: AuthenticationService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.fetchPendingConsultants();
    this.fetchApprovedConsultants();
    this.fetchRejectedConsultants();
  }

  fetchPendingConsultants(): void {
    this.loading = true;
    this.errorMessage = '';

    this.authenticationService
      .getConsultantsForVerification('pending', this.page, this.limit)
      .subscribe(
        (resp: any) => {
          this.consultants = resp?.data || [];

          if (resp?.pagination) {
            this.total = resp.pagination.total || 0;
            this.page = resp.pagination.page || this.page;
            this.limit = resp.pagination.limit || this.limit;
            this.totalPages = resp.pagination.totalPages || 1;
          } else {
            this.total = this.consultants.length;
            this.totalPages = 1;
          }

          this.loading = false;
        },
        (error: any) => {
          this.loading = false;
          this.errorMessage =
            error?.error?.message || 'Unable to fetch pending consultants.';
          this.toastr.error(this.errorMessage);
        },
      );
  }

  fetchApprovedConsultants(): void {
    this.approvedLoading = true;
    this.approvedErrorMessage = "";

    this.authenticationService
      .getConsultantsForVerification(
        "approved",
        this.approvedPage,
        this.approvedLimit,
      )
      .subscribe(
        (resp: any) => {
          this.approvedConsultants = resp?.data || [];

          if (resp?.pagination) {
            this.approvedTotal = resp.pagination.total || 0;
            this.approvedPage = resp.pagination.page || this.approvedPage;
            this.approvedLimit = resp.pagination.limit || this.approvedLimit;
            this.approvedTotalPages = resp.pagination.totalPages || 1;
          } else {
            this.approvedTotal = this.approvedConsultants.length;
            this.approvedTotalPages = 1;
          }

          this.approvedLoading = false;
        },
        (error: any) => {
          this.approvedLoading = false;
          this.approvedErrorMessage =
            error?.error?.message || "Unable to fetch approved consultants.";
          this.toastr.error(this.approvedErrorMessage);
        },
      );
  }

  fetchRejectedConsultants(): void {
    this.rejectedLoading = true;
    this.rejectedErrorMessage = "";

    this.authenticationService
      .getConsultantsForVerification(
        "rejected",
        this.rejectedPage,
        this.rejectedLimit,
      )
      .subscribe(
        (resp: any) => {
          this.rejectedConsultants = resp?.data || [];

          if (resp?.pagination) {
            this.rejectedTotal = resp.pagination.total || 0;
            this.rejectedPage = resp.pagination.page || this.rejectedPage;
            this.rejectedLimit = resp.pagination.limit || this.rejectedLimit;
            this.rejectedTotalPages = resp.pagination.totalPages || 1;
          } else {
            this.rejectedTotal = this.rejectedConsultants.length;
            this.rejectedTotalPages = 1;
          }

          this.rejectedLoading = false;
        },
        (error: any) => {
          this.rejectedLoading = false;
          this.rejectedErrorMessage =
            error?.error?.message || "Unable to fetch rejected consultants.";
          this.toastr.error(this.rejectedErrorMessage);
        },
      );
  }

  approveConsultant(consultant: any): void {
    if (!consultant || !consultant._id) {
      return;
    }

    this.updateConsultantStatus(consultant._id, 'approve');
  }

  openRejectModal(consultant: any): void {
    this.selectedConsultant = consultant;
    this.rejectionReason = '';
    this.showRejectDialog = true;
  }

  closeRejectModal(): void {
    this.showRejectDialog = false;
    this.selectedConsultant = null;
    this.rejectionReason = '';
  }

  confirmReject(): void {
    if (!this.selectedConsultant || !this.selectedConsultant._id) {
      return;
    }

    if (!this.rejectionReason.trim()) {
      this.toastr.error('Please provide a rejection reason.');
      return;
    }

    this.updateConsultantStatus(
      this.selectedConsultant._id,
      'reject',
      this.rejectionReason.trim(),
    );
  }

  updateConsultantStatus(
    consultantId: string,
    action: 'approve' | 'reject',
    rejectionReason?: string,
  ): void {
    this.actionLoadingId = consultantId;

    this.authenticationService
      .verifyConsultant(consultantId, action, rejectionReason)
      .subscribe(
        (resp: any) => {
          this.actionLoadingId = '';
          this.toastr.success(
            resp?.message || `Consultant ${action}d successfully.`,
          );

          if (action === 'reject') {
            this.closeRejectModal();
          }

          this.fetchPendingConsultants();
          this.fetchApprovedConsultants();
          this.fetchRejectedConsultants();
        },
        (error: any) => {
          this.actionLoadingId = '';
          const message =
            error?.error?.message || `Unable to ${action} consultant.`;
          this.toastr.error(message);
        },
      );
  }

  changePage(nextPage: number): void {
    if (nextPage < 1 || nextPage > this.totalPages || nextPage === this.page) {
      return;
    }

    this.page = nextPage;
    this.fetchPendingConsultants();
  }

  changeApprovedPage(nextPage: number): void {
    if (
      nextPage < 1 ||
      nextPage > this.approvedTotalPages ||
      nextPage === this.approvedPage
    ) {
      return;
    }

    this.approvedPage = nextPage;
    this.fetchApprovedConsultants();
  }

  changeRejectedPage(nextPage: number): void {
    if (
      nextPage < 1 ||
      nextPage > this.rejectedTotalPages ||
      nextPage === this.rejectedPage
    ) {
      return;
    }

    this.rejectedPage = nextPage;
    this.fetchRejectedConsultants();
  }

  isActionLoading(consultantId: string): boolean {
    return this.actionLoadingId === consultantId;
  }

  getDocumentUrl(path: string): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const base = (this.authenticationService.baseUrl || '').replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${base}/${normalizedPath}`;
  }

  getDocumentName(document: any, index: number): string {
    if (document?.name && String(document.name).trim()) {
      return document.name;
    }

    return `Document ${index + 1}`;
  }

  isImageFile(fileName: string, path: string): boolean {
    const source = (fileName || path || '').toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(source);
  }

  getFeeSummary(consultant: any): string {
    const fees = consultant?.fees;

    if (!fees) {
      return '-';
    }

    const chat =
      fees.chat !== undefined && fees.chat !== null ? fees.chat : '-';
    const audio =
      fees.audio !== undefined && fees.audio !== null ? fees.audio : '-';
    const video =
      fees.video !== undefined && fees.video !== null ? fees.video : '-';
    const currency = fees.currency || 'USD';

    return `Chat: ${chat}, Audio: ${audio}, Video: ${video} ${currency}`;
  }

  getFeeValue(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    return String(value);
  }

  getFeeCurrency(consultant: any): string {
    return consultant?.fees?.currency || 'USD';
  }

  trackByConsultant(index: number, consultant: any): string {
    return consultant?._id || String(index);
  }

  trackByDocument(index: number, document: any): string {
    return document?._id || String(index);
  }
}
